import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Reorder, useDragControls } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  BookOpen, GraduationCap, Users, Library, Plus, Loader2, Layers,
  Search, LayoutGrid, List as ListIcon, SlidersHorizontal, DoorOpen, Pencil,
  GripVertical, ArrowDownAZ, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", section: "A", class_teacher: "", academic_session: "2026-27", room: "", capacity: 40, status: "Active" };

const PRESET_CLASSES = [
  "Nursery", "LKG", "UKG", "1st", "2nd", "3rd", "4th", "5th", "6th",
  "7th", "8th", "9th", "10th", "11th", "12th",
];
const PRESET_ORDER = Object.fromEntries(PRESET_CLASSES.map((n, i) => [n, i]));

const naturalCmp = (a, b) =>
  (PRESET_ORDER[a.name] ?? 99) - (PRESET_ORDER[b.name] ?? 99) ||
  a.name.localeCompare(b.name) || (a.section || "").localeCompare(b.section || "");
const hasOrder = (c) => c.sort_order !== undefined && c.sort_order !== null;
const orderCmp = (a, b) => {
  if (hasOrder(a) && hasOrder(b)) return a.sort_order - b.sort_order;
  if (hasOrder(a)) return -1;
  if (hasOrder(b)) return 1;
  return naturalCmp(a, b);
};

const SUBJECT_COLORS = [
  "bg-blue-50 text-blue-700", "bg-emerald-50 text-emerald-700", "bg-purple-50 text-purple-700",
  "bg-amber-50 text-amber-700", "bg-rose-50 text-rose-700", "bg-cyan-50 text-cyan-700",
  "bg-indigo-50 text-indigo-700", "bg-pink-50 text-pink-700",
];
const subjColor = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return SUBJECT_COLORS[h % SUBJECT_COLORS.length];
};

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4" data-testid={`class-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${tint}`}><Icon size={22} /></div>
      <div>
        <div className="text-sm text-slate-500">{label}</div>
        <div className="font-heading text-3xl font-extrabold text-slate-900 leading-tight">{value}</div>
      </div>
    </div>
  );
}

function SubjectTags({ subjects }) {
  if (!subjects || !subjects.length) return null;
  const shown = subjects.slice(0, 4);
  const extra = subjects.length - shown.length;
  return (
    <div className="flex flex-wrap gap-1.5 mt-3">
      {shown.map((s) => (
        <span key={s} className={`text-[11px] font-medium px-2 py-0.5 rounded-md ${subjColor(s)}`}>{s}</span>
      ))}
      {extra > 0 && <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-slate-100 text-slate-500">+{extra}</span>}
    </div>
  );
}

function Capacity({ c }) {
  const cap = c.capacity || 0;
  const pct = cap > 0 ? Math.min(100, Math.round((c.student_count / cap) * 100)) : 0;
  const barColor = pct >= 90 ? "bg-rose-500" : pct >= 75 ? "bg-amber-500" : "bg-orange-400";
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
        <span>{cap > 0 ? `${c.student_count} / ${cap} students` : `${c.student_count} students`}</span>
        {cap > 0 && <span className="font-medium">{pct}% full</span>}
      </div>
      {cap > 0 && (
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

function RowBody({ c, canManage, user, nav, openEdit, handle, isAdmin, askDelete }) {
  return (
    <div className="flex flex-wrap items-center gap-4 p-4" data-testid={`class-row-${c.name}`}>
      {handle}
      <div className="min-w-[160px]">
        <div className="font-heading font-bold text-slate-900">{c.name}{c.section ? `-${c.section}` : ""}</div>
        <div className="text-xs text-slate-500">{c.class_teacher || "No teacher"}</div>
      </div>
      <div className="flex items-center gap-1.5 text-sm text-slate-500 w-24"><DoorOpen size={14} />{c.room || "—"}</div>
      <div className="flex items-center gap-1.5 text-sm text-slate-500 w-24"><BookOpen size={14} />{c.subject_count} subj</div>
      <div className="flex-1 min-w-[160px]"><Capacity c={c} /></div>
      <div className="flex gap-2 ml-auto">
        <Button size="sm" variant="outline" onClick={() => nav(`/students?class=${c.id}`)}>Students</Button>
        {canManage && <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-class-list-${c.name}`}><Pencil size={14} /></Button>}
        {isAdmin && <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => askDelete(c)} data-testid={`delete-class-list-${c.name}`}><Trash2 size={14} /></Button>}
      </div>
    </div>
  );
}

function DraggableRow({ c, canManage, user, nav, openEdit, onDrop, isAdmin, askDelete }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={c}
      dragListener={false}
      dragControls={controls}
      onDragEnd={() => onDrop && onDrop()}
      className="bg-white"
      data-testid={`class-reorder-${c.name}`}
    >
      <RowBody
        c={c} canManage={canManage} user={user} nav={nav} openEdit={openEdit} isAdmin={isAdmin} askDelete={askDelete}
        handle={
          <button
            type="button"
            onPointerDown={(e) => controls.start(e)}
            className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 touch-none"
            title="Drag to reorder"
            data-testid={`drag-handle-${c.name}`}
          >
            <GripVertical size={18} />
          </button>
        }
      />
    </Reorder.Item>
  );
}

export default function Classes() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canManage = hasPerm(user, "manage_settings");
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [list, setList] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [query, setQuery] = useState("");
  const [grade, setGrade] = useState("all");
  const [view, setView] = useState("grid");

  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [edit, setEdit] = useState(null);
  const [selSubjects, setSelSubjects] = useState([]);
  const [saving, setSaving] = useState(false);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSel, setBulkSel] = useState([]);
  const [bulk, setBulk] = useState({ sections: "A", class_teacher: "", academic_session: "2026-27", capacity: 40 });
  const [bulkSaving, setBulkSaving] = useState(false);

  const orderRef = useRef([]);
  useEffect(() => { orderRef.current = list || []; }, [list]);

  const load = () => api.get("/classes").then((r) => setList([...r.data].sort(orderCmp)));
  const [sections, setSections] = useState([]);
  useEffect(() => {
    load();
    api.get("/subjects").then((r) => setSubjects(r.data)).catch(() => {});
    api.get("/staff").then((r) => setTeachers(r.data.filter((s) => s.role !== "admin"))).catch(() => {});
    api.get("/sections").then((r) => setSections(r.data.sections || [])).catch(() => {});
  }, []);

  const [newSection, setNewSection] = useState("");
  const addSection = async () => {
    const v = newSection.trim();
    if (!v) return;
    if (sections.some((s) => s.toLowerCase() === v.toLowerCase())) {
      toast.error("Section already exists");
      return;
    }
    const next = [...sections, v];
    setSections(next);
    setNewSection("");
    if (!f.section) setF((prev) => ({ ...prev, section: v }));
    try { await api.put("/sections", { items: next }); toast.success(`Section "${v}" created`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const removeSection = async (sec) => {
    const next = sections.filter((s) => s !== sec);
    setSections(next);
    if (f.section === sec) setF((prev) => ({ ...prev, section: "" }));
    try { await api.put("/sections", { items: next }); toast.success(`Section "${sec}" removed`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const persistOrder = async (arr) => {
    try { await api.put("/classes/reorder", { ordered_ids: arr.map((c) => c.id) }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const handleReorder = (newArr) => setList(newArr);
  const onDrop = () => persistOrder(orderRef.current);

  const autoSort = async () => {
    if (!list) return;
    const natural = [...list].sort(naturalCmp);
    setList(natural);
    await persistOrder(natural);
    toast.success("Classes sorted Nursery → 12th");
  };

  const openEdit = async (c) => {
    setEdit(c);
    setF({ ...empty, ...c, capacity: c.capacity || 0 });
    setSelSubjects([]);
    setOpen(true);
    try {
      const r = await api.get(`/classes/${c.id}/subjects`);
      setSelSubjects(r.data.subject_ids || []);
    } catch { /* ignore */ }
  };
  const openAdd = () => { setEdit(null); setF(empty); setSelSubjects([]); setOpen(true); };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Class name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...f, section: (f.section || "").trim(), capacity: Number(f.capacity) || 0 };
      delete payload.sort_order; delete payload.subjects; delete payload.subject_count; delete payload.student_count; delete payload.id;
      // Persist a newly typed section to the global sections master list
      if (payload.section && !sections.some((s) => s.toLowerCase() === payload.section.toLowerCase())) {
        const next = [...sections, payload.section];
        setSections(next);
        try { await api.put("/sections", { items: next }); } catch { /* ignore */ }
      }
      let cid = edit?.id;
      if (edit) await api.put(`/classes/${edit.id}`, payload);
      else { const r = await api.post("/classes", payload); cid = r.data.id; }
      if (cid && subjects.length) {
        try { await api.put(`/classes/${cid}/subjects`, { subject_ids: selSubjects }); } catch { /* ignore */ }
      }
      toast.success("Saved"); setOpen(false); setEdit(null); setF(empty); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const [delTarget, setDelTarget] = useState(null);
  const askDelete = (c) => setDelTarget(c);
  const confirmDelete = async () => {
    try { await api.delete(`/classes/${delTarget.id}`); toast.success("Class deleted"); setDelTarget(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const toggleSubject = (id) =>
    setSelSubjects((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const openBulk = () => {
    setBulkSel([]);
    setBulk({ sections: "A", class_teacher: "", academic_session: "2026-27", capacity: 40 });
    setBulkOpen(true);
  };
  const toggleBulk = (name) =>
    setBulkSel((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  const allSelected = bulkSel.length === PRESET_CLASSES.length;
  const toggleAll = () => setBulkSel(allSelected ? [] : [...PRESET_CLASSES]);

  const saveBulk = async () => {
    if (!bulkSel.length) { toast.error("Select at least one class"); return; }
    const sections = bulk.sections.split(",").map((s) => s.trim()).filter(Boolean);
    if (!sections.length) { toast.error("Enter at least one section"); return; }
    if (!bulk.academic_session.trim()) { toast.error("Academic session is required"); return; }
    setBulkSaving(true);
    try {
      const r = await api.post("/classes/bulk", {
        names: bulkSel, sections, class_teacher: bulk.class_teacher,
        academic_session: bulk.academic_session.trim(), capacity: Number(bulk.capacity) || 0,
      });
      toast.success(`${r.data.created} class(es) added${r.data.skipped ? `, ${r.data.skipped} skipped (already exist)` : ""}`);
      setBulkOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBulkSaving(false); }
  };

  const grades = useMemo(() => {
    if (!list) return [];
    const names = [...new Set(list.map((c) => c.name))];
    return names.sort((a, b) => (PRESET_ORDER[a] ?? 99) - (PRESET_ORDER[b] ?? 99) || a.localeCompare(b));
  }, [list]);

  // Keep current (custom/natural) list order; only filter.
  const filtered = useMemo(() => {
    if (!list) return [];
    const q = query.trim().toLowerCase();
    return list
      .filter((c) => (grade === "all" || c.name === grade))
      .filter((c) => !q || `${c.name} ${c.section} ${c.class_teacher} ${c.room}`.toLowerCase().includes(q));
  }, [list, query, grade]);

  if (!list) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  const totalStudents = list.reduce((s, c) => s + (c.student_count || 0), 0);
  const totalCapacity = list.reduce((s, c) => s + (c.capacity || 0), 0);
  const draggable = canManage && view === "list" && !query.trim() && grade === "all";

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={BookOpen} label="Total Classes" value={list.length} tint="bg-amber-50 text-amber-600" />
        <StatCard icon={GraduationCap} label="Total Students" value={totalStudents} tint="bg-indigo-50 text-indigo-600" />
        <StatCard icon={Users} label="Total Capacity" value={totalCapacity} tint="bg-blue-50 text-blue-600" />
        <StatCard icon={Library} label="Subjects Offered" value={subjects.length} tint="bg-purple-50 text-purple-600" />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search classes..." className="pl-9" data-testid="class-search" />
        </div>
        <Select value={grade} onValueChange={setGrade}>
          <SelectTrigger className="w-40" data-testid="class-grade-filter"><SelectValue placeholder="All Grades" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Grades</SelectItem>
            {grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          <button type="button" onClick={() => setView("grid")} className={`px-2.5 py-2 ${view === "grid" ? "bg-indigo-600 text-white" : "bg-white text-slate-500"}`} data-testid="class-view-grid"><LayoutGrid size={16} /></button>
          <button type="button" onClick={() => setView("list")} className={`px-2.5 py-2 ${view === "list" ? "bg-indigo-600 text-white" : "bg-white text-slate-500"}`} data-testid="class-view-list"><ListIcon size={16} /></button>
        </div>
        <div className="flex gap-2 sm:ml-auto">
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="class-settings-btn"><SlidersHorizontal size={16} className="mr-2" />Class Settings</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={openBulk} data-testid="add-bulk-classes-btn"><Layers size={15} className="mr-2" />Add Bulk Classes</DropdownMenuItem>
                <DropdownMenuItem onClick={autoSort} data-testid="auto-sort-classes-btn"><ArrowDownAZ size={15} className="mr-2" />Auto-sort (Nursery → 12th)</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canManage && <Button onClick={openAdd} data-testid="add-class-btn"><Plus size={18} className="mr-2" />Add Class</Button>}
        </div>
      </div>

      {draggable && (
        <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="reorder-hint">
          <GripVertical size={14} className="text-slate-400" />
          Drag rows by the handle to set a custom class order. Your order is saved and used across the app.
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-20 text-slate-400">No classes found</div>
      ) : view === "grid" ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-all group" data-testid={`class-card-${c.name}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div>
                    <div className="font-heading text-lg font-bold text-slate-900">{c.name}{c.section ? `-${c.section}` : ""}</div>
                    <div className="text-sm text-slate-500">{c.class_teacher || "No teacher assigned"}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => nav(`/students?class=${c.id}`)} data-testid={`view-students-${c.name}`}>Students</Button>
                </div>
                <div className="flex items-center gap-1">
                  {canManage && (
                    <button type="button" onClick={() => openEdit(c)} className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-indigo-600" data-testid={`edit-class-${c.name}`}><Pencil size={15} /></button>
                  )}
                  {isAdmin && (
                    <button type="button" onClick={() => askDelete(c)} className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-rose-600" data-testid={`delete-class-${c.name}`}><Trash2 size={15} /></button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-500 mt-4">
                <span className="flex items-center gap-1.5"><DoorOpen size={14} />{c.room || "—"}</span>
                <span className="flex items-center gap-1.5"><BookOpen size={14} />{c.subject_count} subjects</span>
              </div>
              <Capacity c={c} />
              <SubjectTags subjects={c.subjects} />
            </div>
          ))}
        </div>
      ) : draggable ? (
        <Reorder.Group axis="y" values={filtered} onReorder={handleReorder} className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden" data-testid="class-reorder-list">
          {filtered.map((c) => (
            <DraggableRow key={c.id} c={c} canManage={canManage} user={user} nav={nav} openEdit={openEdit} onDrop={onDrop} isAdmin={isAdmin} askDelete={askDelete} />
          ))}
        </Reorder.Group>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
          {filtered.map((c) => (
            <RowBody key={c.id} c={c} canManage={canManage} user={user} nav={nav} openEdit={openEdit} isAdmin={isAdmin} askDelete={askDelete} />
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{edit ? "Edit Class" : "Add Class"}</DialogTitle>
            <DialogDescription>Set the class details, room, capacity and offered subjects.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Class Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="class-name-input" /></div>
              <div><Label>Section</Label>
                <Select value={f.section || "none"} onValueChange={(v) => setF({ ...f, section: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1" data-testid="class-section-input"><SelectValue placeholder="Select section" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {sections.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {canManage && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="section-manager">
                <Label className="text-xs text-slate-500">Manage Sections</Label>
                <div className="flex gap-2">
                  <Input value={newSection} onChange={(e) => setNewSection(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSection(); } }}
                    placeholder="New section name (e.g. D)" className="h-9" data-testid="new-section-input" />
                  <Button type="button" variant="outline" className="h-9 shrink-0" onClick={addSection} data-testid="create-section-btn">
                    <Plus size={15} className="mr-1" />Create Section
                  </Button>
                </div>
                {sections.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {sections.map((s) => (
                      <span key={s} className="inline-flex items-center gap-1 text-xs font-medium pl-2.5 pr-1 py-1 rounded-md border border-slate-200 bg-white text-slate-700" data-testid={`section-chip-${s}`}>
                        {s}
                        <button type="button" onClick={() => removeSection(s)} className="ml-0.5 p-1 rounded text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors" data-testid={`section-chip-remove-${s}`} aria-label={`Remove section ${s}`}>
                          <Trash2 size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : <p className="text-xs text-slate-400">No sections yet. Create one above.</p>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Room</Label><Input value={f.room} onChange={(e) => setF({ ...f, room: e.target.value })} placeholder="Room 101" data-testid="class-room-input" /></div>
              <div><Label>Capacity</Label><Input type="number" min="0" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} data-testid="class-capacity-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Class Teacher</Label>
                <Select value={f.class_teacher || "none"} onValueChange={(v) => setF({ ...f, class_teacher: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1" data-testid="class-teacher-input"><SelectValue placeholder="Select teacher" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {teachers.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Academic Session</Label><Input value={f.academic_session} onChange={(e) => setF({ ...f, academic_session: e.target.value })} /></div>
            </div>
            {subjects.length > 0 && (
              <div>
                <Label>Subjects</Label>
                <div className="flex flex-wrap gap-2 mt-1.5 max-h-40 overflow-y-auto">
                  {subjects.map((s) => {
                    const on = selSubjects.includes(s.id);
                    return (
                      <button key={s.id} type="button" onClick={() => toggleSubject(s.id)}
                        className={`text-xs font-medium px-2.5 py-1 rounded-md border transition-colors ${on ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                        data-testid={`class-subject-${s.name}`}>{s.name}</button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-class-btn">{saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Bulk Classes</DialogTitle>
            <DialogDescription>Select preset classes and create one per section at once.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Select Classes</Label>
              <button type="button" onClick={toggleAll} className="text-xs font-semibold text-indigo-600 hover:underline" data-testid="bulk-select-all">
                {allSelected ? "Clear All" : "Select All"}
              </button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-56 overflow-y-auto pr-1">
              {PRESET_CLASSES.map((name) => {
                const on = bulkSel.includes(name);
                return (
                  <label key={name}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${on ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`}
                    data-testid={`bulk-class-${name}`}>
                    <Checkbox checked={on} onCheckedChange={() => toggleBulk(name)} />
                    <span className="text-sm font-medium text-slate-700">{name}</span>
                  </label>
                );
              })}
            </div>
            <div className="grid sm:grid-cols-4 gap-3">
              <div>
                <Label>Section(s)</Label>
                <Input value={bulk.sections} onChange={(e) => setBulk({ ...bulk, sections: e.target.value })} placeholder="A, B, C" data-testid="bulk-sections-input" />
              </div>
              <div>
                <Label>Capacity</Label>
                <Input type="number" min="0" value={bulk.capacity} onChange={(e) => setBulk({ ...bulk, capacity: e.target.value })} data-testid="bulk-capacity-input" />
              </div>
              <div>
                <Label>Class Teacher</Label>
                <Input value={bulk.class_teacher} onChange={(e) => setBulk({ ...bulk, class_teacher: e.target.value })} data-testid="bulk-teacher-input" />
              </div>
              <div>
                <Label>Academic Session</Label>
                <Input value={bulk.academic_session} onChange={(e) => setBulk({ ...bulk, academic_session: e.target.value })} data-testid="bulk-session-input" />
              </div>
            </div>
            <p className="text-xs text-slate-500">{bulkSel.length} class(es) selected × {bulk.sections.split(",").map((s) => s.trim()).filter(Boolean).length || 0} section(s)</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={saveBulk} disabled={bulkSaving} data-testid="save-bulk-classes-btn">
              {bulkSaving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Layers size={16} className="mr-2" />}Create Classes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="max-w-md" data-testid="delete-class-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-600">Delete Class?</DialogTitle>
            <DialogDescription>This will permanently delete <b>{delTarget?.name}{delTarget?.section ? `-${delTarget.section}` : ""}</b>. This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={confirmDelete} data-testid="confirm-delete-class-btn">Delete Class</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
